import { CSSProperties, useCallback, useMemo, useRef } from "react";
import { Cell, Row } from "react-aria-components";

import TableContextProvider from "components/Table/Context";
import Table from "components/Table";
import TableRow from "components/Table/Row";
import useTableSort from "components/Table/useTableSort";
import TableWrapper from "components/Table/Wrapper";
import EmptyState from "ds/components/EmptyState";
import {
  EmptystateSearchNoResultsColored,
  EmptystateUsedByColored,
} from "components/icons/generated";
import PageInfo from "components/PageWrapper/Info";
import PageWrapperTitle from "components/PageWrapper/Title";
import SearchInput from "components/SearchInput";
import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";
import usePolledQuery from "apollo/usePolledQuery";
import { PollingIntervalGroups } from "apollo/constants";
import { SpacesContext } from "views/Account/SpacesProvider";
import useErrorHandle from "hooks/useErrorHandle";
import PageLoading from "components/loading/PageLoading";
import NotFoundPage from "components/error/NotFoundPage";

import RoleUsedByListItem from "./ListItem";
import {
  FILTERS_ORDER_SETTINGS_KEY,
  INITIAL_COLUMNS_VIEW_CONFIG,
  initialSortDirection,
  initialSortOption,
  USED_BY_COLUMN_CONFIG,
  USED_BY_COLUMNS_STORAGE_KEY,
  USED_BY_CUSTOM_END_COLUMN_CONFIG,
  ROW_HEADER_ID,
  SORT_CONFIG,
} from "./constants";
import { ANALYTICS } from "../constants";
import { useFilteredEntities } from "./useFilteredEntities";
import { GET_ENTITIES_WITH_ROLE_BINDINGS } from "./gql";
import { EntityWithRoleBinding } from "./types";
import { getEntities } from "./helpers";
import { RoleView } from "../types";

type RoleUsedByProps = {
  role: RoleView;
};

const RoleUsedBy = ({ role }: RoleUsedByProps) => {
  const virtualizedListContainerRef = useRef<HTMLDivElement | null>(null);

  const { onError } = useTypedContext(FlashContext);
  const { spaces } = useTypedContext(SpacesContext);

  const { data, loading, error } = usePolledQuery(GET_ENTITIES_WITH_ROLE_BINDINGS, {
    onError,
    pollingGroup: PollingIntervalGroups.Default,
  });

  const { handleSortOptionChange, sortDescriptor } = useTableSort({
    filtersOrderSettingsKey: FILTERS_ORDER_SETTINGS_KEY,
    sortConfig: SORT_CONFIG,
    initialSortOption,
    initialSortDirection,
  });

  // TODO: improve when BE provide proper query
  const entities = useMemo(() => getEntities(data, spaces, role), [data, spaces, role]);

  const renderRow = useCallback(
    (
      item: Partial<EntityWithRoleBinding> & {
        virtIndex?: number;
        style?: CSSProperties;
        virtKey?: string;
        id: string;
        height?: number;
        ref?: (node: Element | null | undefined) => void;
      }
    ) => {
      if ("height" in item && (item.id === "after" || item.id === "before")) {
        return (
          <Row>
            <Cell style={{ height: item.height }} />
          </Row>
        );
      }

      return (
        <TableRow
          index={item.virtIndex}
          ref={item.ref}
          style={item.style}
          key={item.virtKey}
          id={item.id}
          name={item.id}
        >
          <RoleUsedByListItem entity={item as EntityWithRoleBinding} />
        </TableRow>
      );
    },
    []
  );

  const filteredEntities = useFilteredEntities(entities, sortDescriptor);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (!data?.managedUsers) {
    return loading ? <PageLoading /> : <NotFoundPage />;
  }

  return (
    <>
      <PageInfo title={<PageWrapperTitle>Used by</PageWrapperTitle>}>
        <SearchInput placeholder="Search..." filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY} />
      </PageInfo>
      <TableContextProvider
        columnsConfig={USED_BY_COLUMN_CONFIG}
        endCustomColumns={USED_BY_CUSTOM_END_COLUMN_CONFIG}
        initialColumnViewConfig={INITIAL_COLUMNS_VIEW_CONFIG}
        rowHeaderId={ROW_HEADER_ID}
        localStorageId={USED_BY_COLUMNS_STORAGE_KEY}
        setSortDescriptor={handleSortOptionChange}
        sortDescriptor={sortDescriptor}
        {...ANALYTICS}
      >
        {!!filteredEntities?.length && (
          <TableWrapper virtualizedListContainerRef={virtualizedListContainerRef}>
            <Table ariaLabel="Used by" items={filteredEntities} ref={virtualizedListContainerRef}>
              {renderRow}
            </Table>
          </TableWrapper>
        )}
        {!filteredEntities.length && !entities?.length && (
          <EmptyState title="You don’t have any assignments yet" icon={EmptystateUsedByColored} />
        )}
        {!filteredEntities.length && !!entities?.length && (
          <EmptyState title="No results" icon={EmptystateSearchNoResultsColored} announce />
        )}
      </TableContextProvider>
    </>
  );
};

export default RoleUsedBy;
